import { Add, Delete } from '@mui/icons-material';
import { IconButton, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import usePoll from '../../../hooks/usePoll';
import { EventPollQuestion } from '../../../interfaces/interfaces';
import { useSelector } from '../../../reduxToolkit/typedRedux';
import { randomString } from '../../../utils/functions';
import VyButton from '../VyButton';
import VyTextField from '../VyTextField';
import VyTooltip from '../VyTooltip';

interface PollAddProps {
  onSubmit: (b: { control: boolean; add: boolean }) => void;
  type: 'stage' | 'meeting';
  backgroundColor?: string;
  textColor?: string;
}

const PollAdd = ({ onSubmit, type, backgroundColor, textColor }: PollAddProps) => {
  const event = useSelector((state) => state.common.event ?? state.admin.event);
  const { t } = useTranslation();
  const { addPollQuestion, getPollBackgroundColor, getPollTextColor } = usePoll();

  const { register, handleSubmit, reset } = useForm<EventPollQuestion>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [answers, setAnswers] = useState<any>([]);

  const submit = async (data: EventPollQuestion) => {
    if (answers.length === 0) toast(t('poll:info-poll-no-answer'));
    setIsLoading(true);
    await addPollQuestion({ ...data, isActive: isVisible, answers: answers }, type, () => setAnswers(null));
    setIsLoading(false);
    onSubmit({ control: true, add: false });
    reset();
    setAnswers([]);
  };

  function storeAnswers(e: any, i: number) {
    const _answers = [...answers];
    _answers[i] = {
      ..._answers[i],
      answer: e.target.value,
    };
    setAnswers([..._answers]);
  }

  function deleteAnswer(i: number) {
    const _answers = [...answers];
    _answers.splice(i, 1);
    setAnswers([..._answers].map((a, i) => ({ ...a, sortIndex: i })));
  }

  return (
    <>
      <div
        style={{
          background: getPollBackgroundColor(false, backgroundColor),
          color: getPollTextColor(false, textColor),
        }}
        className={`flex flex-col justify-center items-center p-2 mt-4  rounded-lg `}>
        <form onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit(submit)} className="flex flex-col w-full h-full form">
          <div className="flex overflow-y-scroll flex-col items-center px-1 w-full">
            <div className="py-2 w-full">
              <VyTooltip title={t('poll:tooltip-question')}>
                <Typography>{t('poll:question')}</Typography>
              </VyTooltip>
            </div>
            <VyTextField register={register('content')} fullWidth disabled={isLoading} style={{ backgroundColor: 'white' }} inputProps={{ maxLength: 255 }} />

            {/* commented out for now because it was disfunctional */}
            {/* <FormControlLabel
            control={
              <TemplateSwitch
                style={{ color: event?.template.mainButtonTextColor }}
                checked={isVisible}
                onChange={() => setIsVisible(!isVisible)}
                name="isActive"
                color="primary"
                defaultChecked={false}
                disabled={true}
              />
            }
            label={<Typography>{t('poll:is-active')}</Typography>}
          /> */}

            <div className="flex items-center mt-4 w-full">
              <VyTooltip title={t('poll:tooltip-answers')}>
                <Typography>{t('poll:answers')}</Typography>
              </VyTooltip>
            </div>

            {answers?.map((answer: any, i: number) => (
              <div key={`poll-answer-${answer.id}`} className="flex flex-row justify-center items-center space-x-2 w-full">
                <div className="flex flex-col my-2 space-y-4 w-full">
                  <VyTextField
                    fullWidth
                    disabled={isLoading}
                    style={{ backgroundColor: 'white' }}
                    inputProps={{ maxLength: 255 }}
                    required
                    defaultValue={answer.answer}
                    onBlur={(e) => {
                      storeAnswers(e, i);
                    }}
                  />
                </div>
                <IconButton
                  color="error"
                  onClick={() => {
                    deleteAnswer(i);
                  }}>
                  <Delete />
                </IconButton>
              </div>
            ))}
            <VyTooltip title={t('poll:poll-new-answer')}>
              <IconButton
                onClick={() => {
                  const _answers = [...answers];
                  const id = randomString(5); // used to define the block key
                  _answers[_answers.length] = { id, answer: '', sortIndex: _answers.length };
                  setAnswers(_answers);
                }}
                disabled={isLoading}
                style={{ color: getPollTextColor(false, textColor) }}
                size="large">
                <Add />
              </IconButton>
            </VyTooltip>
          </div>

          <VyButton
            disabled={isLoading}
            type="submit"
            showLoadingIcon={isLoading}
            event={event}
            style={{
              border: `1px solid ${getPollTextColor(false, textColor)}`,
              color: getPollTextColor(false, textColor),
              backgroundColor: getPollBackgroundColor(false, backgroundColor),
            }}>
            {t('poll:create')}
          </VyButton>
        </form>
      </div>
    </>
  );
};

export default PollAdd;
